/**
 * User management contracts — used by the users handler.
 *
 * Covers invitation lifecycle (invite, accept, resend, cancel), user CRUD,
 * and team statistics. Entities themselves are defined in `../entities/`; this
 * module declares the request/response shapes exposed by the Lambda endpoints.
 */
import { z } from 'zod';
import { Email, InvitationId, ISO8601Date, TenantId, TenantSlug, UserId, } from '../primitives/index.js';
import { InviteableRole, Role } from '../enums/index.js';
// ============================================================================
// REQUEST BODIES
// ============================================================================
/** `POST /users` request body — invite a new user to the caller's tenant. */
export const InviteUserRequestSchema = z.object({
    email: Email,
    name: z.string().min(1).max(100).optional(),
    role: InviteableRole,
});
/**
 * `POST /users/accept-invite` request body — served without auth.
 *
 * Unlike `InvitationAcceptSchema` (which treats `name` as optional because the
 * invite may have carried it), the endpoint requires `name` because the
 * Cognito AdminCreateUser call needs a non-empty display name.
 */
export const AcceptInviteRequestSchema = z.object({
    token: z.string().min(32).max(64),
    name: z.string().min(1).max(100),
    password: z.string().min(8).max(128),
});
/**
 * `PUT /users/:id` request body — all fields optional. The handler rejects
 * bodies where every field is undefined with a 400.
 */
export const UpdateUserRequestSchema = z
    .object({
    name: z.string().min(1).max(100),
    phone: z.string().max(20).nullable(),
    role: InviteableRole,
    active: z.boolean(),
})
    .partial();
// ============================================================================
// RESPONSE BODIES
// ============================================================================
/** Row shape in `GET /users` — camelCase canonical. */
export const ListedUserSchema = z.object({
    id: UserId,
    email: Email,
    name: z.string().nullable(),
    phone: z.string().nullable(),
    role: Role,
    active: z.boolean(),
    lastLoginAt: ISO8601Date.nullable(),
    createdAt: ISO8601Date,
    /** Present only in the super_admin cross-tenant view. */
    tenantId: TenantId.nullable().optional(),
    tenantName: z.string().nullable().optional(),
    tenantSlug: TenantSlug.nullable().optional(),
    isSuperAdmin: z.boolean().optional(),
});
export const ListUsersResponseSchema = z.object({
    users: z.array(ListedUserSchema),
    count: z.number().int().nonnegative(),
});
/** Row shape in `GET /users/:id`. */
export const UserDetailSchema = z.object({
    id: UserId,
    email: Email,
    name: z.string().nullable(),
    phone: z.string().nullable(),
    role: Role,
    active: z.boolean(),
    preferences: z.record(z.string(), z.unknown()).nullable(),
    invitedBy: UserId.nullable(),
    invitationAcceptedAt: ISO8601Date.nullable(),
    lastLoginAt: ISO8601Date.nullable(),
    createdAt: ISO8601Date,
    updatedAt: ISO8601Date,
    /** Super-admin-only visibility. */
    tenantId: TenantId.nullable().optional(),
    isSuperAdmin: z.boolean().optional(),
});
export const GetUserResponseSchema = z.object({
    user: UserDetailSchema,
});
/** Success body returned by `POST /users`. */
export const InviteUserResponseSchema = z.object({
    message: z.string().min(1),
    invitation: z.object({
        email: Email,
        role: InviteableRole,
        expiresAt: ISO8601Date,
    }),
});
/** Success body returned by `POST /users/accept-invite`. */
export const AcceptInviteResponseSchema = z.object({
    message: z.string().min(1),
    email: Email,
});
/** Success body returned by `PUT /users/:id`. */
export const UpdateUserResponseSchema = z.object({
    message: z.string().min(1),
    user: z.object({
        id: UserId,
        email: Email,
        name: z.string().nullable(),
        phone: z.string().nullable(),
        role: Role,
        active: z.boolean(),
        updatedAt: ISO8601Date,
    }),
});
/** Success body returned by `DELETE /users/:id`. */
export const DeleteUserResponseSchema = z.object({
    message: z.string().min(1),
    deletedUser: z.object({
        id: UserId,
        email: Email,
    }),
});
/** Row shape in `GET /users/stats` invitations array. */
export const PendingInvitationSchema = z.object({
    id: InvitationId,
    email: Email,
    name: z.string().nullable(),
    role: InviteableRole,
    invitedByName: z.string(),
    expiresAt: ISO8601Date,
    createdAt: ISO8601Date,
    daysUntilExpiry: z.number().int(),
});
export const TeamStatsSchema = z.object({
    totalMembers: z.number().int().nonnegative(),
    activeMembers: z.number().int().nonnegative(),
    pendingInvitations: z.number().int().nonnegative(),
    seatLimit: z.number().int().positive(),
    seatsUsed: z.number().int().nonnegative(),
    seatsAvailable: z.number().int().nonnegative(),
});
/** `GET /users/stats` success body. */
export const TeamStatsResponseSchema = z.object({
    stats: TeamStatsSchema,
    invitations: z.array(PendingInvitationSchema),
});
/** Success body returned by `POST /users/invitations/:id/resend`. */
export const ResendInviteResponseSchema = z.object({
    message: z.string().min(1),
    invitation: z.object({
        id: InvitationId,
        email: Email,
        role: InviteableRole,
        expiresAt: ISO8601Date,
    }),
});
/** Success body returned by `DELETE /users/invitations/:id`. */
export const CancelInviteResponseSchema = z.object({
    message: z.string().min(1),
    invitation: z.object({
        id: InvitationId,
        email: Email,
        status: z.literal('cancelled'),
    }),
});
//# sourceMappingURL=index.js.map